import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, Minus, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiErrorMessage } from "@/lib/api";
import { createSupportTicket, loadFaq, retrieveAnswer } from "@/support/supportApi";
import { buildEscalationTicket } from "@/support/ticketPayload";
import "@/support/support.css";

const WELCOME = "Hi! 👋 I'm the MinutePay Assistant. What can I help you with?";
const CATEGORIES = [
  { id: "getting_started", label: "Getting Started" },
  { id: "tracking_calls", label: "Tracking & Calls" },
  { id: "account_billing", label: "Account & Billing" },
  { id: "something_else", label: "Something Else" },
];
const WHAT_HAPPENED = "What happened?";

export default function SupportWidget() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [faq, setFaq] = useState({ categories: CATEGORIES, articles: [] });
  const [messages, setMessages] = useState([
    { id: "welcome", role: "assistant", content: WELCOME, kind: "welcome" },
  ]);
  const [awaitingProblem, setAwaitingProblem] = useState(false);
  const [ticketSent, setTicketSent] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const scroller = useRef(null);
  const inputRef = useRef(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    if (!open) return;
    loadFaq().then(setFaq).catch(() => {});
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const el = scroller.current;
    if (!el) return;
    const snap = () => {
      el.scrollTop = el.scrollHeight;
    };
    snap();
    const frame = requestAnimationFrame(snap);
    return () => cancelAnimationFrame(frame);
  }, [messages, open]);

  const categoryArticles = useMemo(() => {
    if (!activeCategory || activeCategory === "something_else") return [];
    return (faq.articles || []).filter((a) => a.category_id === activeCategory);
  }, [faq.articles, activeCategory]);

  function push(role, content, extra = {}) {
    setMessages((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random().toString(16).slice(2)}`, role, content, ...extra },
    ]);
  }

  function onPickCategory(cat) {
    setActiveCategory(cat.id);
    setAwaitingProblem(false);
    push("user", cat.label);
    if (cat.id === "something_else") {
      push("assistant", "I couldn't solve this one. Would you like to contact MinutePay Support?", {
        kind: "escalate",
      });
      return;
    }
    const list = (faq.articles || []).filter((a) => a.category_id === cat.id);
    if (!list.length) {
      push("assistant", "I couldn't solve this one. Would you like to contact MinutePay Support?", {
        kind: "escalate",
      });
      return;
    }
    push("assistant", "Here are common questions in that area:", { kind: "articles", articles: list });
  }

  function onPickArticle(article) {
    push("user", article.title);
    push("assistant", article.answer, { articleId: article.id });
  }

  async function onAsk(text) {
    const question = (text || "").trim();
    if (!question || busy) return;
    setDraft("");
    if (awaitingProblem) {
      await submitTicket(question);
      return;
    }
    push("user", question);
    setBusy(true);
    try {
      const result = await retrieveAnswer(question, activeCategory);
      if (result?.matched && result.article) {
        push("assistant", result.article.answer, { articleId: result.article.id });
      } else {
        push(
          "assistant",
          result?.message ||
            "I couldn't find a reliable answer for that. Would you like to contact MinutePay Support?",
          { kind: "escalate" },
        );
      }
    } catch (err) {
      push(
        "assistant",
        "I couldn't find a reliable answer for that. Would you like to contact MinutePay Support?",
        { kind: "escalate" },
      );
      void err;
    } finally {
      setBusy(false);
    }
  }

  function onContactSupport() {
    setAwaitingProblem(true);
    setTicketSent(false);
    setDraft("");
    setMessages((prev) => [
      ...prev.map((m) => (m.kind === "escalate" ? { ...m, kind: "escalate_closed" } : m)),
      {
        id: `${Date.now()}-happened`,
        role: "assistant",
        content: WHAT_HAPPENED,
        kind: "prompt",
      },
    ]);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  async function submitTicket(problem) {
    const text = String(problem || "").trim();
    if (!text) return;
    setBusy(true);
    try {
      const payload = buildEscalationTicket({
        priorMessages: messagesRef.current,
        problem: text,
        category: CATEGORIES.find((c) => c.id === activeCategory)?.label || "Something Else",
      });
      const created = await createSupportTicket(payload);
      setAwaitingProblem(false);
      setTicketSent(true);
      setMessages((prev) => [
        ...prev,
        { id: `${Date.now()}-user-problem`, role: "user", content: text },
        {
          id: `${Date.now()}-ticket`,
          role: "assistant",
          content: created.message || `Your request has been sent to MinutePay Support. Ticket #${created.id}`,
          kind: "ticket",
          ticketId: created.id,
        },
      ]);
    } catch (err) {
      push("assistant", apiErrorMessage(err) || "Could not submit your support request. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  const hideContact = awaitingProblem || ticketSent;
  const netlifyBadgeClear = process.env.NODE_ENV === "production";

  return (
    <div
      className={`mp-support ${open ? "is-open" : ""} ${netlifyBadgeClear ? "mp-support-netlify-clear" : ""}`}
    >
      {open ? (
        <section className="mp-support-panel" aria-label="MinutePay Support">
          <header className="mp-support-head">
            <div>
              <p>MinutePay Support</p>
            </div>
            <div className="mp-support-head-actions">
              <button type="button" aria-label="Minimize support" onClick={() => setOpen(false)}>
                <Minus size={16} />
              </button>
              <button type="button" aria-label="Close support" onClick={() => setOpen(false)}>
                <X size={16} />
              </button>
            </div>
          </header>
          <div className="mp-support-thread" ref={scroller}>
            {messages.map((m) => (
              <div key={m.id} className={`mp-support-bubble is-${m.role}`}>
                <p>{m.content}</p>
                {m.kind === "welcome" ? (
                  <div className="mp-support-cats">
                    {CATEGORIES.map((cat) => (
                      <button key={cat.id} type="button" onClick={() => onPickCategory(cat)}>
                        {cat.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {m.kind === "articles" ? (
                  <div className="mp-support-cats">
                    {(m.articles || categoryArticles).map((article) => (
                      <button key={article.id} type="button" onClick={() => onPickArticle(article)}>
                        {article.title}
                      </button>
                    ))}
                  </div>
                ) : null}
                {m.kind === "escalate" && !hideContact ? (
                  <Button size="sm" className="mp-support-contact" onClick={onContactSupport}>
                    Contact Support
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
          <form
            className="mp-support-composer"
            onSubmit={(e) => {
              e.preventDefault();
              onAsk(draft);
            }}
          >
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={awaitingProblem ? "Describe what happened..." : "Ask a question..."}
              maxLength={500}
              disabled={busy}
              aria-label={awaitingProblem ? "Describe what happened" : "Ask a question"}
            />
            <button type="submit" aria-label="Send" disabled={busy || !draft.trim()}>
              <Send size={16} />
            </button>
          </form>
        </section>
      ) : null}
      {open ? null : (
        <button
          type="button"
          className="mp-support-launcher"
          onClick={() => setOpen(true)}
          aria-expanded={open}
        >
          <MessageCircle size={18} />
          Need help?
        </button>
      )}
    </div>
  );
}
